<template>
    <div class="modern-container">
        <!-- The old view stays mounted for the entire duration of the target
             route's dynamic import (see nav-progress.ts), the only
             signal a user gets that a slow chunk fetch is in progress rather
             than the page being frozen. -->
        <div v-if="isNavigating" class="nav-progress-bar" aria-hidden="true"></div>
        <div id="container">
            <!-- Transition is suppressed for the very first route: it exists to
                 animate BETWEEN routes, and on initial load it would fade the
                 mounted page in over the server-prerendered paint (visible
                 flash on /employer). -->
            <router-view v-slot="{ Component, route }">
                <transition :name="initialRoute ? 'none' : ((route.meta.transition as string) || 'page-fwd')"
                            mode="out-in"
                            @leave="onRouteLeave">
                    <component :is="Component" />
                </transition>
            </router-view>
        </div>
        <!-- WCAG SC 4.1.3 (Level AA): SPA route announcer. Mounted eagerly
             with empty content so assistive tech registers the live region
             at boot — adding it dynamically requires a 2s wait per AT spec.
             aria-live="polite" (not assertive) so route changes never
             interrupt mid-utterance. aria-atomic re-reads the whole region
             on every change. Updated from router.afterEach with a
             setTimeout matching the 320ms enter so the announcement lands
             when the new content is interactive. -->
        <div role="status" aria-live="polite" aria-atomic="true"
             class="sr-only" data-route-announcer>
            {{ routeAnnouncement }}
        </div>

        <!-- Site-wide new-message nudge, suppressed on the Messages routes since
             their own tab badge already says it. Dismissal is keyed to the unread
             count showing at the time, so it stays closed instead of nagging on
             every route change. That count is unread CONVERSATIONS, so dismissing
             it holds until ANOTHER conversation goes unread: further messages in a
             thread you already dismissed do not raise it again, which is the
             intent (you have been told about that thread) but is narrower than
             "any new message". -->
        <div class="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2">
            <div v-if="showCandidateNudge" class="msg-nudge">
                <router-link :to="{ name: 'my-jobs-messages' }" class="msg-nudge-link">
                    <Icon name="message-square" :size="16" />
                    {{ candidateUnread === 1 ? 'You have a new message' : `You have ${candidateUnread} new messages` }}
                </router-link>
                <button type="button" class="msg-nudge-x" aria-label="Dismiss" @click="dismissCandidateNudge">
                    <Icon name="x" :size="15" />
                </button>
            </div>
            <div v-if="showEmployerNudge" class="msg-nudge">
                <router-link :to="{ name: 'employer-messages' }" class="msg-nudge-link">
                    <Icon name="message-square" :size="16" />
                    {{ employerUnread === 1 ? 'New candidate message' : `${employerUnread} new candidate messages` }}
                </router-link>
                <button type="button" class="msg-nudge-x" aria-label="Dismiss" @click="dismissEmployerNudge">
                    <Icon name="x" :size="15" />
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useCandidateMessagesStore } from './stores/candidateMessages';
import { useEmployerMessagesStore } from './stores/employerMessages';
import { isNavigating } from './lib/nav-progress';
import { onRouteLeave } from './lib/route-transition';

declare const window: any;
const BASE = (window.__APP_BASE__ || '/').replace(/\/+$/, '');

// The shared site navbar is server-rendered and its links are plain anchors, so
// leaving Messages for job posts is a full page load that remounts this
// component. Holding the dismissal in memory alone meant it was forgotten on
// every such navigation and the nudge came back for a message already seen.
// sessionStorage keeps it for the browser session, still keyed to the unread
// count so another unread conversation raises it again.
const SEEN_KEY = { candidate: 'jb-nudge-seen-candidate', employer: 'jb-nudge-seen-employer' };

function readSeen(key: string): number {
    try {
        const n = Number(window.sessionStorage.getItem(key));
        return Number.isFinite(n) && n > 0 ? n : 0;
    } catch { return 0; }
}

function writeSeen(key: string, n: number) {
    // Private mode and storage-blocked contexts throw; the nudge just falls back
    // to per-page-load behaviour there.
    try { window.sessionStorage.setItem(key, String(n)); } catch { /* not fatal */ }
}

// Map route names to human phrases for screen-reader announcements. Routes
// without a name fall through to PATH_ANNOUNCEMENTS below.
const ROUTE_ANNOUNCEMENTS: Record<string, string> = {
    'my-jobs-saved': 'Saved jobs',
    'my-jobs-applications': 'My applications',
    'my-jobs-applications-preview': 'Application preview',
    'my-jobs-messages': 'Messages',
    'my-jobs-conversation': 'Conversation',
    'employer-landing': 'Employer landing',
    'employer-onboarding': 'Employer onboarding',
    'employer-dashboard': 'Employer dashboard',
    'employer-inbox': 'Applicants',
    'employer-messages': 'Messages',
    'employer-candidates': 'Talent pool',
    'employer-profile': 'Employer profile',
    'employer-edit-listing': 'Edit listing',
    'employer-team': 'Team settings',
    'employer-billing': 'Billing',
    'company-public': 'Company page',
};

// Path-prefix fallbacks for unnamed routes. First match wins — order matters.
const PATH_ANNOUNCEMENTS: Array<[RegExp, string]> = [
    [/^\/listing\//, 'job listing'],
    [/^\/apply\//, 'Apply to job'],
    [/^\/postjob/, 'Post a job'],
    [/^\/$/, 'Browse jobs'],
];

function announcementFor(name: string | undefined, path: string): string {
    const named = ROUTE_ANNOUNCEMENTS[String(name || '')];
    if (named) return named;
    for (const [re, phrase] of PATH_ANNOUNCEMENTS) if (re.test(path)) return phrase;
    return 'Page loaded';
}

export default defineComponent({
    name: 'App',
    setup() {
        const candidateMessages = useCandidateMessagesStore();
        const employerMessages = useEmployerMessagesStore();
        return {
            isNavigating,
            onRouteLeave,
            candidateUnread: computed(() => candidateMessages.unreadCount),
            employerUnread: computed(() => employerMessages.unreadCount),
            startMessageStores() {
                // Gated so anonymous visitors don't open the [Authorize] hub and log a 401 per page load.
                if (window.__AUTH_STATUS__?.isAuthenticated) candidateMessages.start(BASE);
                if (window.__AUTH_STATUS__?.hasEmployerSession) employerMessages.start(BASE);
            },
        };
    },
    data() {
        return {
            routeAnnouncement: '',
            initialRoute: true,
            // Unread count each nudge was dismissed at. A higher count later means
            // another conversation went unread, so the nudge comes back. Seeded
            // from sessionStorage so a full page load does not forget it.
            candidateDismissedAt: readSeen(SEEN_KEY.candidate),
            employerDismissedAt: readSeen(SEEN_KEY.employer),
        };
    },
    computed: {
        // Both sections render a Messages tab carrying an unread badge on every
        // one of their routes, so a nudge anywhere inside them just says it twice.
        inCandidateSection(): boolean {
            return (this.$route.path || '').startsWith('/myjobs');
        },
        inEmployerSection(): boolean {
            return (this.$route.path || '').startsWith('/employer');
        },
        // Neither nudge shows inside EITHER workspace, not just its own. One
        // account can hold both sessions, and a floating "you have a new message"
        // while someone is working in the employer dash points at the other side
        // of the product entirely. Inside a workspace the tab badge is the signal;
        // the nudge exists for when you are somewhere else, like browsing jobs.
        inAnyWorkspace(): boolean {
            return this.inCandidateSection || this.inEmployerSection;
        },
        showCandidateNudge(): boolean {
            return this.candidateUnread > this.candidateDismissedAt && !this.inAnyWorkspace;
        },
        showEmployerNudge(): boolean {
            return this.employerUnread > this.employerDismissedAt && !this.inAnyWorkspace;
        },
    },
    methods: {
        // Every write goes through these two so nothing can set the flag without
        // also persisting it.
        setCandidateSeen(n: number) {
            this.candidateDismissedAt = n;
            writeSeen(SEEN_KEY.candidate, n);
        },
        setEmployerSeen(n: number) {
            this.employerDismissedAt = n;
            writeSeen(SEEN_KEY.employer, n);
        },
        dismissCandidateNudge() {
            this.setCandidateSeen(this.candidateUnread);
        },
        dismissEmployerNudge() {
            this.setEmployerSeen(this.employerUnread);
        },
        // Being in a section counts as having seen the badge, so dismiss rather
        // than merely hide: navigating back out must not re-raise the same nudge.
        // Each side dismisses only its OWN nudge, even though both are hidden in
        // either workspace. The employer dash shows no candidate badge, so
        // dismissing the candidate nudge there would bury a message nobody saw.
        dismissNudgesSeenInSection() {
            if (this.inCandidateSection) this.setCandidateSeen(this.candidateUnread);
            if (this.inEmployerSection) this.setEmployerSeen(this.employerUnread);
        },
    },
    mounted() {
        // Flip after the router's initial navigation (incl. lazy chunk) has
        // inserted the first component — later navigations animate normally.
        this.$router.isReady().then(() => {
            this.$nextTick(() => { this.initialRoute = false; });
        });
        this.startMessageStores();
    },
    watch: {
        // Reading everything clears the dismissal, so the next message nudges
        // again rather than being suppressed forever by one earlier dismiss.
        // The counts arrive asynchronously, so re-check the section here too:
        // otherwise a count landing while already inside one flashes the nudge.
        candidateUnread(n: number) {
            if (n === 0) this.setCandidateSeen(0);
            else this.dismissNudgesSeenInSection();
        },
        employerUnread(n: number) {
            if (n === 0) this.setEmployerSeen(0);
            else this.dismissNudgesSeenInSection();
        },
        // Watch path (not just name) so unnamed routes still trigger updates.
        // Using path means /listing/:id-A → /listing/:id-B fires too, which is
        // correct: SR users want to know the page content changed.
        '$route.path': {
            immediate: true,
            handler(_to: string, from: string | undefined) {
                this.dismissNudgesSeenInSection();
                // Skip the announcement on the initial resolve (no previous
                // route): nothing changed yet for a screen reader to hear.
                if (from === undefined) return;
                // Defer the announcement until the enter transition completes
                // (320ms). Announcing earlier means SR users hear the new page
                // name while the old content is still on screen.
                const r = this.$route;
                const phrase = announcementFor(r.name as string | undefined, r.path || '');
                window.setTimeout(() => { this.routeAnnouncement = phrase; }, 320);
            },
        },
    },
});
</script>

<style scoped>
.nav-progress-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    /* Above the shared site navbar, which is sticky at z-index 1030. At a
       lower value the bar renders behind it and is never seen. */
    z-index: 1040;
    background: rgba(var(--jb-primary-rgb), 0.15);
    overflow: hidden;
}
.nav-progress-bar::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -30%;
    width: 30%;
    background: var(--jb-primary);
    animation: nav-progress-slide 1.1s ease-in-out infinite;
}
@keyframes nav-progress-slide {
    0% { left: -30%; }
    100% { left: 100%; }
}
@media (prefers-reduced-motion: reduce) {
    .nav-progress-bar::after { animation: none; left: 0; width: 100%; }
}

/* Notify pill, matched to the "Get a demo" button on scholaro.com. */
.msg-nudge {
    display: flex;
    align-items: stretch;
    overflow: hidden;
    border: 1px solid var(--jb-notify-border);
    border-radius: var(--jb-radius);
    background: var(--jb-notify);
    box-shadow: 0 4px 12px rgba(var(--jb-ink-rgb), 0.15);
}
.msg-nudge-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    font-size: 14px;
    font-weight: 600;
    color: var(--jb-notify-ink);
    text-decoration: none;
}
.msg-nudge-link:hover { background: rgba(0, 0, 0, 0.05); }
.msg-nudge-x {
    display: flex;
    align-items: center;
    padding: 0 10px;
    border: 0;
    border-left: 1px solid var(--jb-notify-border);
    background: transparent;
    color: var(--jb-notify-ink);
    cursor: pointer;
}
.msg-nudge-x:hover { background: rgba(0, 0, 0, 0.08); }
</style>
